import React from 'react';
import {Instagram, LinkedIn, Facebook, Language, Visibility} from '@mui/icons-material';
import { Card, CardContent, Typography, Box, IconButton, Tooltip, Stack, useTheme, alpha } from '@mui/material';
import { ReactComponent as Twitter } from '../../../assets/images/twitter.svg';
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";

const SocialLinksBox = (props) => {
  const { links: socialLinks } = props.tutor
  const count = socialLinks ? Object.keys(socialLinks).filter(key => socialLinks[key]).length : 0
  const navigate = useNavigate()
  const theme = useTheme()
  const {
    userId, status, isGuest
  } = useSelector((state) => state.main);

  const socialLinksData = [
    {
      key: 'facebook',
      icon: <Facebook />,
      label: 'Facebook',
      color: '#1877F2',
      url: socialLinks?.facebook
    },
    {
      key: 'instagram',
      icon: <Instagram />,
      label: 'Instagram',
      color: '#E4405F',
      url: socialLinks?.instagram
    },
    {
      key: 'x',
      icon: <Twitter />,
      label: 'X',
      color: '#000000',
      url: socialLinks?.x
    },
    {
      key: 'linkedin',
      icon: <LinkedIn />,
      label: 'LinkedIn',
      color: '#0A66C2',
      url: socialLinks?.linkedin
    },
    {
      key: 'website',
      icon: <Language />,
      label: 'Website',
      color: '#712d94',
      url: socialLinks?.website
    }
  ];

  const canAccessLinks = userId && (!isGuest || status === 1);

  return (
    <Card sx={{ 
      borderRadius: 3,
      boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.08)}`,
      border: `1px solid ${theme.palette.divider}`,
      overflow: 'hidden'
    }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h5" sx={{ 
            fontWeight: 700, 
            color: theme.palette.text.primary,
            fontSize: { xs: '1.25rem', sm: '1.35rem', md: '1.5rem' },
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 40,
              height: 3,
              backgroundColor: theme.palette.primary.main,
              borderRadius: 2
            }
          }}>
            My social links
          </Typography>
          {!canAccessLinks && (
            <Tooltip title="Login to access social profiles" arrow>
              <IconButton 
                size="small" 
                onClick={() => navigate('/signin')}
                sx={{
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    color: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {count > 0 ? (
          <Stack spacing={1}>
            {socialLinksData.map((link) => {
              if (!link.url) return null;
              
              const content = (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1, sm: 1.5 },
                  p: { xs: 1.25, sm: 1.5 },
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  transition: 'all 0.2s ease',
                  '&:hover': canAccessLinks ? {
                    backgroundColor: alpha(theme.palette.background.paper, 0.8),
                    transform: 'translateY(-1px)',
                    boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`
                  } : {},
                  cursor: canAccessLinks ? 'pointer' : 'default'
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: { xs: 28, sm: 32 },
                    height: { xs: 28, sm: 32 },
                    borderRadius: 1,
                    backgroundColor: alpha(link.color, 0.1),
                    color: link.color,
                    flexShrink: 0,
                    '& svg': {
                      fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }
                  }}>
                    {link.icon}
                  </Box>
                  <Typography variant="body2" sx={{ 
                    color: canAccessLinks ? theme.palette.text.primary : theme.palette.text.secondary,
                    fontWeight: 500,
                    fontSize: { xs: '0.875rem', sm: '0.9rem' }
                  }}>
                    {link.label}
                  </Typography>
                </Box>
              );

              return canAccessLinks ? (
                <a 
                  key={link.key}
                  href={link.url.match(/^https?:\/\//) ? link.url : `//${link.url}`}
                  target="_blank" 
                  rel="noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  {content}
                </a>
              ) : (
                <Box key={link.key}>
                  {content}
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Box sx={{ 
            textAlign: 'center', 
            py: 3,
            color: theme.palette.text.secondary
          }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
              No social links available
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default SocialLinksBox;
